import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import AuthImage from "@/components/students/AuthImage";
import StudentFormDialog from "@/components/students/StudentFormDialog";
import { STUDENT_STATUSES, STATUS_STYLE, PAYMENT_STYLE, DOC_TYPES, fmtMoney } from "@/lib/studentConstants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Loader2, Pencil, Printer, Camera, Upload, FileText, Download,
  Trash2, Activity, ShieldCheck, Phone, Mail, MapPin, Calendar, Clock, CheckCircle2, ClipboardList,
} from "lucide-react";

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm text-secondary">{value || "—"}</p>
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");
  const [editOpen, setEditOpen] = useState(false);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const photoRef = useRef();
  const docRef = useRef();

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/students/${id}`);
      setData(data);
    } catch {
      toast.error("Student not found");
      navigate("/students");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }

  const s = data.student;

  const saveStudent = async (payload, sid) => {
    await api.put(`/students/${sid}`, payload);
    toast.success("Profile updated");
    load(true);
  };

  const changeStatus = async (status) => {
    await api.patch(`/students/${id}/status`, { status });
    toast.success(`Status updated to ${status}`);
    load(true);
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/students/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      setPhotoVersion((v) => v + 1);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    }
    e.target.value = "";
  };

  const onDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", docType);
    setUploadingDoc(true);
    try {
      await api.post(`/students/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${docType} uploaded`);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingDoc(false);
      e.target.value = "";
    }
  };

  const openDoc = async (doc) => {
    try {
      const res = await api.get(`/students/${id}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };

  const confirmDeleteDoc = async () => {
    await api.delete(`/students/${id}/documents/${deleteDoc.id}`);
    toast.success("Document removed");
    setDeleteDoc(null);
    load(true);
  };

  return (
    <div data-testid="student-profile-page" id="print-area">
      <div className="mb-4 flex items-center justify-between no-print">
        <Button variant="ghost" size="sm" onClick={() => navigate("/students")} data-testid="back-to-students">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Students
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-profile"><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
          <Button size="sm" onClick={() => setEditOpen(true)} data-testid="edit-student-button"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
        </div>
      </div>

      {/* Header card */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <div className="relative">
            <AuthImage path={s.photo_url ? `/students/${id}/photo/view?v=${photoVersion}` : null} className="h-24 w-24 rounded-2xl object-cover" alt={s.full_name} />
            <button onClick={() => photoRef.current?.click()} className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-md transition-transform hover:scale-105 no-print" data-testid="upload-photo-button" title="Upload photo">
              <Camera className="h-4 w-4" />
            </button>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="photo-input" />
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-slate-400">{s.student_code}</span>
              <Badge className={`${STATUS_STYLE[s.status]} border-0`}>{s.status}</Badge>
              {s.job_guarantee_eligible && <Badge className="border-0 bg-accent/15 text-green-700"><ShieldCheck className="mr-1 h-3 w-3" /> Job Guarantee</Badge>}
            </div>
            <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-secondary">{s.full_name}</h1>
            <p className="text-sm text-muted-foreground">{s.course_enrolled || "No course assigned"}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {s.mobile}</span>
              {s.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {s.email}</span>}
              {(s.city || s.state) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[s.city, s.state].filter(Boolean).join(", ")}</span>}
            </div>
          </div>
          <div className="w-full sm:w-48 no-print">
            <Label className="text-xs">Student Status</Label>
            <Select value={s.status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="profile-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STUDENT_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Fee summary */}
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4"><p className="text-xs uppercase text-muted-foreground">Final Fee</p><p className="mt-1 font-display text-xl font-bold text-secondary">{fmtMoney(s.final_fee)}</p></div>
        <div className="rounded-xl border border-border bg-card p-4"><p className="text-xs uppercase text-muted-foreground">Total Paid</p><p className="mt-1 font-display text-xl font-bold text-accent">{fmtMoney(s.total_paid)}</p></div>
        <div className="rounded-xl border border-border bg-card p-4"><p className="text-xs uppercase text-muted-foreground">Pending</p><p className="mt-1 font-display text-xl font-bold text-rose-600">{fmtMoney(s.pending_amount)}</p></div>
        <div className="rounded-xl border border-border bg-card p-4"><p className="text-xs uppercase text-muted-foreground">Payment</p><Badge className={`${PAYMENT_STYLE[s.payment_status]} mt-2 border-0`}>{s.payment_status}</Badge></div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="no-print">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Fees & Payments</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({data.documents.length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 font-display text-sm font-semibold text-secondary">Personal Details</h3>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Info label="Gender" value={s.gender} />
              <Info label="Date of Birth" value={s.dob} />
              <Info label="Alt. Mobile" value={s.alt_mobile} />
              <Info label="Pincode" value={s.pincode} />
              <Info label="Address" value={s.address} />
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 font-display text-sm font-semibold text-secondary">Course & Fees</h3>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Info label="Course" value={s.course_enrolled} />
              <Info label="Course Fee" value={fmtMoney(s.course_fee)} />
              <Info label="Discount" value={fmtMoney(s.discount)} />
              <Info label="Admission Date" value={s.admission_date} />
              <Info label="Counselor" value={s.admission_counselor} />
              <Info label="Next Due Amount" value={s.next_due_amount ? fmtMoney(s.next_due_amount) : "—"} />
              <Info label="Next Due Date" value={s.next_due_date} />
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 font-display text-sm font-semibold text-secondary">Batch & Training</h3>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Info label="Batch" value={s.batch} />
              <Info label="Batch Timing" value={s.batch_timing} />
              <Info label="Batch Start" value={s.batch_start_date} />
              <Info label="Trainer" value={s.trainer} />
              <Info label="Mode" value={s.mode} />
              <Info label="Expected Completion" value={s.expected_completion_date} />
              <Info label="Job Guarantee" value={s.job_guarantee_eligible ? "Yes" : "No"} />
            </div>
            {s.remarks && <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700"><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Remarks</p>{s.remarks}</div>}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4 space-y-4" data-testid="tab-payments-content">
          {(!data.enrollments || data.enrollments.length === 0) ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-muted-foreground" data-testid="enrollments-empty">
              <ClipboardList className="h-8 w-8" /><p>No enrollment yet for this student.</p>
            </div>
          ) : (
            data.enrollments.map(({ enrollment: e, payments, installments }) => (
              <div key={e.id} className="rounded-xl border border-border bg-card p-6" data-testid={`enrollment-card-${e.enrollment_code}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-400">{e.enrollment_code}</span>
                      <Badge className="border-0 bg-primary/10 text-primary">{e.academic_status}</Badge>
                      <Badge className={`${PAYMENT_STYLE[e.payment_status] || ""} border-0`}>{e.payment_status}</Badge>
                    </div>
                    <h3 className="mt-1 font-display text-base font-semibold text-secondary">{e.course_name}</h3>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <Info label="Batch" value={e.batch_name} />
                  <Info label="Trainer" value={e.trainer_name} />
                  <Info label="Course Fee" value={fmtMoney(e.course_fee)} />
                  <Info label="Discount" value={fmtMoney(e.discount)} />
                  <Info label="Final Fee" value={fmtMoney(e.final_fee)} />
                  <Info label="Amount Paid" value={fmtMoney(e.total_paid)} />
                  <Info label="Pending Amount" value={fmtMoney(e.pending_amount)} />
                  <Info label="Next Due Date" value={e.next_due_date} />
                </div>

                {installments && installments.length > 0 && (
                  <div className="mt-5">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Installment Plan</p>
                    <div className="overflow-hidden rounded-lg border border-border">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground">
                          <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Due Date</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th></tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {installments.map((i, idx) => (
                            <tr key={i.id} data-testid={`installment-${i.id}`}>
                              <td className="px-3 py-2 text-slate-500">{i.seq || idx + 1}</td>
                              <td className="px-3 py-2">{i.due_date}</td>
                              <td className="px-3 py-2 font-medium">{fmtMoney(i.due_amount)}</td>
                              <td className="px-3 py-2"><Badge className={`${PAYMENT_STYLE[i.status] || "bg-slate-100 text-slate-600"} border-0`}>{i.status}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="mt-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment History</p>
                  {(!payments || payments.length === 0) ? (
                    <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">No payments recorded yet.</p>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-border">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground">
                          <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Receipt</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Mode</th><th className="px-3 py-2 text-right">Amount</th></tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {payments.map((p) => (
                            <tr key={p.id} data-testid={`payment-${p.id}`}>
                              <td className="px-3 py-2">{String(p.payment_date || "").slice(0, 10)}</td>
                              <td className="px-3 py-2 font-mono text-xs text-slate-500">{p.receipt_number || "—"}</td>
                              <td className="px-3 py-2">{p.type}</td>
                              <td className="px-3 py-2">{p.payment_mode}</td>
                              <td className="px-3 py-2 text-right font-medium text-accent">{fmtMoney(p.amount_received)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="mb-4 flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-end no-print">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => docRef.current?.click()} disabled={uploadingDoc} data-testid="upload-doc-button">
              {uploadingDoc ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Upload Document
            </Button>
            <input ref={docRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden" onChange={onDoc} data-testid="doc-input" />
          </div>

          {data.documents.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-muted-foreground" data-testid="documents-empty">
              <FileText className="h-8 w-8" /><p>No documents uploaded yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.documents.map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4" data-testid={`document-${doc.id}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-secondary">{doc.doc_type}</p>
                    <p className="truncate text-xs text-muted-foreground">{doc.original_filename}</p>
                    <p className="text-[11px] text-slate-400">{new Date(doc.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-1 no-print">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDoc(doc)} data-testid={`view-doc-${doc.id}`}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteDoc(doc)} data-testid={`delete-doc-${doc.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <div className="rounded-xl border border-border bg-card p-6" data-testid="student-timeline">
            <div className="space-y-4">
              {data.activities.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
              {data.activities.map((a) => (
                <div key={a.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary/10"><Activity className="h-3.5 w-3.5 text-primary" /></span>
                    <span className="my-1 w-px flex-1 bg-border" />
                  </div>
                  <div className="min-w-0 pb-2">
                    <p className="text-sm text-secondary">{a.description}</p>
                    <p className="text-xs text-muted-foreground"><span className="font-medium text-primary">{a.type}</span> · {a.user_name} · {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <StudentFormDialog open={editOpen} onOpenChange={setEditOpen} student={s} onSave={saveStudent} />

      <AlertDialog open={Boolean(deleteDoc)} onOpenChange={(v) => { if (!v) setDeleteDoc(null); }}>
        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this document?</AlertDialogTitle>
            <AlertDialogDescription>{deleteDoc?.doc_type} — {deleteDoc?.original_filename} will be removed from this student's profile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDoc} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-doc">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
